import React, { useState, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  ReferenceLine, Cell, Customized,
} from 'recharts';

// Match styling from other charts in Wholesale.js
const AXIS_TICK_COLOR = '#555';
const EVENT_LABEL_SIZE = 13;

// Iran war event marker
const IRAN_WAR_DATE = '2026-02-28';

// --- Electricity constants ---
const ELEC_DEMAND_WEIGHTS = { Q1: 0.2874, Q2: 0.2202, Q3: 0.2139, Q4: 0.2785 };
const PEAK_WEIGHT = 0.30;
const BASE_WEIGHT = 0.70;
const ELEC_SHAPING = 1.0738;

// --- Gas constants ---
const GAS_DEMAND_WEIGHTS = { Q1: 0.4357, Q2: 0.1538, Q3: 0.0683, Q4: 0.3422 };
const GAS_SHAPING = 1.06622;
const GAS_UIG = 1.01860;
const GAS_TOTAL_MULTIPLIER = GAS_SHAPING * GAS_UIG; // ~1.0861

// p/therm → £/MWh conversion
const PTHERM_TO_MWH = 10 / 29.3071; // ~0.34121

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// --- Window generation ---
const WINDOW_DATES = {
  Q1: { startMonth: 7, startDay: 19, endMonth: 10, endDay: 17 },
  Q2: { startMonth: 10, startDay: 18, endMonth: 1, endDay: 17 },
  Q3: { startMonth: 1, startDay: 18, endMonth: 4, endDay: 18 },
  Q4: { startMonth: 4, startDay: 19, endMonth: 7, endDay: 18 },
};

function buildWindow(capQ, capYear) {
  const qNum = parseInt(capQ[1]);
  const dates = WINDOW_DATES[capQ];

  let startYear = capYear;
  let endYear = capYear;
  if (capQ === 'Q1') { startYear = capYear - 1; endYear = capYear - 1; }
  if (capQ === 'Q2') { startYear = capYear - 1; }

  const start = `${startYear}-${String(dates.startMonth + 1).padStart(2, '0')}-${String(dates.startDay).padStart(2, '0')}`;
  const end = `${endYear}-${String(dates.endMonth + 1).padStart(2, '0')}-${String(dates.endDay).padStart(2, '0')}`;

  const firstMonth = (qNum - 1) * 3;
  const label = `${MONTH_NAMES[firstMonth]} ${String(capYear).slice(2)}`;

  // 4 delivery quarters starting from the cap quarter
  const quarters = [];
  for (let i = 0; i < 4; i++) {
    const dq = ((qNum - 1 + i) % 4) + 1;
    const dy = capYear + Math.floor((qNum - 1 + i) / 4);
    const yr = String(dy).slice(2);
    const months = [];
    const baseMonth = (dq - 1) * 3;
    for (let m = 0; m < 3; m++) {
      months.push(`${MONTH_NAMES[baseMonth + m]} ${yr}`);
    }
    quarters.push({ name: `Q${dq} ${yr}`, months });
  }

  return { cap: `${capQ} ${capYear}`, label, start, end, quarters };
}

function getCurrentAndPreviousWindow() {
  const today = new Date().toISOString().slice(0, 10);
  const candidates = [];
  for (let y = 2025; y <= 2030; y++) {
    for (const q of ['Q1', 'Q2', 'Q3', 'Q4']) {
      candidates.push(buildWindow(q, y));
    }
  }
  for (let i = 0; i < candidates.length; i++) {
    if (today <= candidates[i].end) {
      return { current: candidates[i], previous: candidates[i - 1] || null };
    }
  }
  const last = candidates.length - 1;
  return { current: candidates[last], previous: candidates[last - 1] || null };
}

function getQuarterWeightKeys(win) {
  const startQ = parseInt(win.quarters[0].name[1]);
  return Array.from({ length: 4 }, (_, i) => `Q${((startQ - 1 + i) % 4) + 1}`);
}

// --- Electricity index ---
function computeElecIndex(date, baseData, peakData, win) {
  const basePrices = baseData[date];
  const peakPrices = peakData[date];
  if (!basePrices || !peakPrices) return null;

  const qKeys = getQuarterWeightKeys(win);
  let wBase = 0, wPeak = 0, totalW = 0;

  for (let i = 0; i < win.quarters.length; i++) {
    const q = win.quarters[i];
    const w = ELEC_DEMAND_WEIGHTS[qKeys[i]];
    const bPrices = q.months.map(m => basePrices[m]).filter(p => p != null);
    const pPrices = q.months.map(m => peakPrices[m]).filter(p => p != null);
    if (bPrices.length === 0 || pPrices.length === 0) continue;
    wBase += (bPrices.reduce((a, b) => a + b, 0) / bPrices.length) * w;
    wPeak += (pPrices.reduce((a, b) => a + b, 0) / pPrices.length) * w;
    totalW += w;
  }
  if (totalW === 0) return null;
  return ((wBase / totalW) * BASE_WEIGHT + (wPeak / totalW) * PEAK_WEIGHT) * ELEC_SHAPING;
}

// --- Gas index (result in p/therm) ---
// Tries quarterly strips first (e.g. "Q3 26"), falls back to averaging
// monthly contracts (e.g. avg of "Jul 26","Aug 26","Sep 26") if not available.
function computeGasIndex(date, gasData, win) {
  const prices = gasData[date];
  if (!prices) return null;

  const qKeys = getQuarterWeightKeys(win);
  let weighted = 0, totalW = 0;

  for (let i = 0; i < win.quarters.length; i++) {
    const q = win.quarters[i];
    const w = GAS_DEMAND_WEIGHTS[qKeys[i]];

    // Try quarterly strip first
    let price = prices[q.name];

    // Fall back to averaging monthly contracts
    if (price == null && q.months.length > 0) {
      const monthPrices = q.months.map(m => prices[m]).filter(p => p != null);
      if (monthPrices.length > 0) {
        price = monthPrices.reduce((a, b) => a + b, 0) / monthPrices.length;
      }
    }

    if (price == null) continue;
    weighted += price * w;
    totalW += w;
  }
  if (totalW === 0) return null;
  return (weighted / totalW) * GAS_TOTAL_MULTIPLIER; // p/therm
}

// --- Shared utilities ---
function isWeekday(dateStr) {
  const d = new Date(dateStr + 'T12:00:00Z');
  const day = d.getUTCDay();
  return day !== 0 && day !== 6;
}

function generateTradingDays(start, end) {
  const days = [];
  const current = new Date(start + 'T12:00:00Z');
  const endDate = new Date(end + 'T12:00:00Z');
  while (current <= endDate) {
    const dateStr = current.toISOString().slice(0, 10);
    if (isWeekday(dateStr)) days.push(dateStr);
    current.setUTCDate(current.getUTCDate() + 1);
  }
  return days;
}

function formatDateLabel(dateStr) {
  const d = new Date(dateStr + 'T12:00:00Z');
  return `${d.getUTCDate()} ${MONTH_NAMES[d.getUTCMonth()]}`;
}

function snapToTradingDay(targetDate, tradingDays) {
  let closest = tradingDays[0];
  let minDiff = Infinity;
  for (const d of tradingDays) {
    const diff = Math.abs(new Date(d) - new Date(targetDate));
    if (diff < minDiff) { minDiff = diff; closest = d; }
  }
  return closest;
}

function buildChartData(win, indexFn) {
  const tradingDays = generateTradingDays(win.start, win.end);
  const today = new Date().toISOString().slice(0, 10);

  const raw = tradingDays.map(date => {
    const index = indexFn(date);
    const isPast = date <= today;
    return { date, index, isPast };
  });

  const observed = raw.filter(d => d.index != null && d.isPast);
  const avg = observed.length > 0
    ? observed.reduce((s, d) => s + d.index, 0) / observed.length
    : null;

  return raw.map(d => ({
    date: d.date,
    value: (d.index != null && d.isPast) ? d.index : avg,
    isObserved: d.index != null && d.isPast,
  }));
}

function computeWindowAvg(win, indexFn) {
  const tradingDays = generateTradingDays(win.start, win.end);
  const vals = tradingDays.map(d => indexFn(d)).filter(v => v != null);
  return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
}

// --- Tooltip ---
function ObsTooltip({ active, payload, unit, color }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d || d.value == null) return null;
  return (
    <div style={{
      background: 'white', border: '1px solid #ddd', borderRadius: 6,
      padding: '8px 12px', fontSize: '0.85em', boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    }}>
      <div style={{ fontWeight: 600, marginBottom: 2 }}>{formatDateLabel(d.date)}</div>
      <div style={{ color: d.isObserved ? color : '#bbb' }}>
        {unit === 'ptherm'
          ? `${Math.round(d.value)} p/therm`
          : `£${Math.round(d.value)}/MWh`}
      </div>
    </div>
  );
}

// --- Reusable observation window chart ---
function ObservationChart({ data, runningAvg, prevAvg, prevLabel, observedCount, totalDays,
  iranWarDay, color, unit, yDomain, source }) {

  const fmtVal = (v) => unit === 'ptherm' ? `${Math.round(v)}p` : `£${Math.round(v)}`;
  const fmtLabel = (v) => unit === 'ptherm' ? `${Math.round(v)} p/therm` : `£${Math.round(v)}/MWh`;

  return (
    <>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data} margin={{ top: 15, right: 15, left: 10, bottom: 5 }} barCategoryGap="15%">
          <XAxis
            dataKey="date"
            tickFormatter={formatDateLabel}
            tick={{ fontSize: 12, fill: AXIS_TICK_COLOR }}
            ticks={[data[0]?.date, data[data.length - 1]?.date]}
            axisLine={{ stroke: '#222' }}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: AXIS_TICK_COLOR }}
            axisLine={false} tickLine={false} width={50}
            domain={yDomain}
            tickFormatter={fmtVal}
          />
          <Tooltip content={<ObsTooltip unit={unit} color={color} />} cursor={false} />

          <Bar dataKey="value" radius={[2, 2, 0, 0]}>
            {data.map((d, i) => (
              <Cell key={i} fill={d.isObserved ? color : '#f0f0f0'} />
            ))}
          </Bar>

          {prevAvg != null && (
            <ReferenceLine y={prevAvg} stroke="#aaa" strokeDasharray="4 3" strokeWidth={1.5} />
          )}
          {runningAvg != null && (
            <ReferenceLine y={runningAvg} stroke={color} strokeDasharray="6 4" strokeWidth={2} />
          )}
          {iranWarDay && (
            <ReferenceLine x={iranWarDay} stroke={AXIS_TICK_COLOR} strokeDasharray="4 3" strokeWidth={1} />
          )}

          <Customized component={(props) => {
            const { yAxisMap, xAxisMap, offset } = props;
            const yAxis = yAxisMap && Object.values(yAxisMap)[0];
            const xAxis = xAxisMap && Object.values(xAxisMap)[0];
            if (!yAxis || !xAxis) return null;

            const chartRight = offset.left + offset.width - 6;
            const labels = [];

            if (runningAvg != null) {
              const y = yAxis.scale(runningAvg);
              labels.push(
                <g key="avg" transform={`translate(${chartRight}, ${y})`}>
                  <text fill={color} fontSize={11} fontWeight={600} textAnchor="end" dominantBaseline="middle">
                    <tspan x={0} dy={-7}>{fmtLabel(runningAvg)}</tspan>
                    <tspan x={0} dy={14}>{`${observedCount}/${totalDays} days`}</tspan>
                  </text>
                </g>
              );
            }
            if (prevAvg != null && prevLabel) {
              const y = yAxis.scale(prevAvg);
              labels.push(
                <g key="prev" transform={`translate(${chartRight}, ${y})`}>
                  <text fill="#888" fontSize={11} textAnchor="end" dominantBaseline="middle">
                    <tspan x={0} dy={-7}>{fmtLabel(prevAvg)}</tspan>
                    <tspan x={0} dy={14}>{`${prevLabel} cap`}</tspan>
                  </text>
                </g>
              );
            }
            if (iranWarDay && xAxis.scale) {
              const x = xAxis.scale(iranWarDay) + (xAxis.bandSize ? xAxis.bandSize / 2 : 0);
              labels.push(
                <text key="iran" x={x + 4} y={offset.top + 2} fill={AXIS_TICK_COLOR}
                  fontSize={EVENT_LABEL_SIZE} dominantBaseline="hanging">
                  Iran war
                </text>
              );
            }
            return <g>{labels}</g>;
          }} />
        </BarChart>
      </ResponsiveContainer>
      <div style={{ textAlign: 'right', fontSize: '0.75em', color: '#aaa', marginTop: 2, paddingRight: 4 }}>
        Source: {source}
      </div>
    </>
  );
}

// --- Main component ---
// fuel: "electricity" | "gas"
export default function PriceCap({ futuresData, fuel }) {
  const { current: win, previous: prevWin } = useMemo(() => getCurrentAndPreviousWindow(), []);
  const [gasUnit, setGasUnit] = useState('ptherm');

  // --- Compute data based on fuel type ---
  const chartDataRaw = useMemo(() => {
    if (fuel === 'electricity') {
      if (!futuresData?.base || !futuresData?.peak) return [];
      const { data: baseD } = futuresData.base;
      const { data: peakD } = futuresData.peak;
      return buildChartData(win, (date) => computeElecIndex(date, baseD, peakD, win));
    } else {
      if (!futuresData?.gas) return [];
      return buildChartData(win, (date) => computeGasIndex(date, futuresData.gas.data, win));
    }
  }, [futuresData, win, fuel]);

  // Gas unit conversion (no-op for electricity)
  const gasConv = fuel === 'gas' && gasUnit === 'mwh' ? PTHERM_TO_MWH : 1;
  const chartData = useMemo(() => {
    if (fuel === 'electricity' || gasConv === 1) return chartDataRaw;
    return chartDataRaw.map(d => ({ ...d, value: d.value != null ? d.value * gasConv : null }));
  }, [chartDataRaw, gasConv, fuel]);

  const runningAvg = useMemo(() => {
    const obs = chartData.filter(d => d.isObserved).map(d => d.value);
    return obs.length > 0 ? obs.reduce((a, b) => a + b, 0) / obs.length : null;
  }, [chartData]);

  const prevAvgRaw = useMemo(() => {
    if (!prevWin) return null;
    if (fuel === 'electricity') {
      if (!futuresData?.base || !futuresData?.peak) return null;
      const { data: baseD } = futuresData.base;
      const { data: peakD } = futuresData.peak;
      return computeWindowAvg(prevWin, (d) => computeElecIndex(d, baseD, peakD, prevWin));
    } else {
      if (!futuresData?.gas) return null;
      return computeWindowAvg(prevWin, (d) => computeGasIndex(d, futuresData.gas.data, prevWin));
    }
  }, [futuresData, prevWin, fuel]);
  const prevAvg = prevAvgRaw != null ? prevAvgRaw * gasConv : null;

  const observedCount = chartData.filter(d => d.isObserved).length;
  const totalDays = chartData.length;

  const iranWarDay = useMemo(() => {
    if (chartData.length === 0) return null;
    const days = chartData.map(d => d.date);
    if (IRAN_WAR_DATE >= win.start && IRAN_WAR_DATE <= win.end) {
      return snapToTradingDay(IRAN_WAR_DATE, days);
    }
    return null;
  }, [chartData, win]);

  const yDomain = useMemo(() => {
    const vals = chartData.map(d => d.value).filter(v => v != null);
    if (prevAvg != null) vals.push(prevAvg);
    if (vals.length === 0) return ['auto', 'auto'];
    return [Math.floor(Math.min(...vals) - 5), Math.ceil(Math.max(...vals) + 5)];
  }, [chartData, prevAvg]);

  if (!futuresData || chartData.length === 0) return null;

  const isElec = fuel === 'electricity';
  const color = isElec ? '#1460F9' : '#f07856';
  const unit = isElec ? 'mwh' : gasUnit;
  const source = isElec ? 'ICE UK Electricity Futures' : 'ICE UK Natural Gas Futures';

  return (
    <section className="wh-section">
      <div className="wh-section-header">
        <h2>{isElec ? 'Electricity' : 'Gas'} — Upcoming Price Cap ({win.label})</h2>
        <span className="wh-section-subtitle" style={{ maxWidth: 560, display: 'inline-block' }}>
          {isElec
            ? 'The wholesale component of the next price cap is determined by a 3 month observation window of power futures.'
            : 'The wholesale component of the next price cap is determined by a 3 month observation window of NBP gas futures.'}
        </span>
      </div>

      <div className="wh-chart-card">
        {!isElec && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
            <div className="wh-granularity-toggle">
              <button
                className={`wh-range-btn ${gasUnit === 'ptherm' ? 'active' : ''}`}
                onClick={() => setGasUnit('ptherm')}
              >p/therm</button>
              <button
                className={`wh-range-btn ${gasUnit === 'mwh' ? 'active' : ''}`}
                onClick={() => setGasUnit('mwh')}
              >£/MWh</button>
            </div>
          </div>
        )}
        <ObservationChart
          data={chartData} runningAvg={runningAvg} prevAvg={prevAvg}
          prevLabel={prevWin?.label} observedCount={observedCount} totalDays={totalDays}
          iranWarDay={iranWarDay} color={color} unit={unit}
          yDomain={yDomain} source={source}
        />
      </div>
    </section>
  );
}
